import { Button, Field, Icons, StatusStrip, TextArea, TextButton, TextInput } from "../../../ds";
import { ChangesetConflictDTO, ResolutionChoices } from "../models/ChangeRequestDTO";
import { Fragment, useState } from "react";
import { Resolution, Resolutions, conflictKey } from "../utils/conflicts";

import { SideLabels } from "../utils/labels";
import { diffWordsWithSpace } from "diff";

/** Pages linked by name in the summary; the tree marks every one. */
const SummaryPages = 5;
/** Conflicts shown at first on a page; the rest behind "Show more". */
const FirstShown = 5;
/** Past this many conflicts the progress is a count, not one pip each. */
const MaxPips = 12;

const plural = (n: number, one: string, many: string) => (n === 1 ? `1 ${one}` : `${n} ${many}`);

/** One pip per conflict, filled once resolved ("●●○"); a count past a dozen. */
export function ResolvedPips(props: { total: number; resolved: number }) {
    const label = `${props.resolved} of ${plural(props.total, "conflict", "conflicts")} resolved`;
    return (
        <span className="cr-pips" role="img" aria-label={label}>
            {props.total <= MaxPips
                ? Array.from({ length: props.total }, (_, i) => <i key={i} className={i < props.resolved ? "is-done" : undefined} />)
                : <span className="cr-pips-text">{props.resolved}/{props.total}</span>}
        </span>
    );
}

interface ISummaryProps {
    conflicts: ChangesetConflictDTO[];
    /** How many of them the author has settled. */
    resolved: number;
    isAuthor: boolean;
    /** The author's name, for a reviewer's wording. */
    authorName?: string;
    /** True when an Update ran into them (not only the check): nothing was changed. */
    attempted: boolean;
    titleOf: (pageId: number) => string;
    onSelectPage: (pageId: number) => void;
    /** A reviewer who will not wait: reject with the conflicts as the reason. */
    onRejectWithConflicts?: () => void;
    /** Runs the update check again (the pages may have moved on). */
    onCheckAgain?: () => void;
}

/**
 * The header's strip about the conflicts: how many, on which pages (each opens that page, where they are shown and resolved), and what
 * to do next. The details stay in the page pane so the header keeps its size whatever the count.
 */
export function RebaseConflictsSummary(props: ISummaryProps) {
    const counts = new Map<number, number>();
    for (const c of props.conflicts) counts.set(c.pageId, (counts.get(c.pageId) ?? 0) + 1);
    const pages = Array.from(counts.entries());
    const hidden = pages.length - SummaryPages;
    const left = props.conflicts.length - props.resolved;
    const blocked = props.conflicts.some((c) => c.code === "deleted" || c.code === "no-base");
    const author = props.authorName ?? "the author";
    return (
        <section className="cr-strip" aria-label="Conflicts">
            <StatusStrip
                tone="warn"
                aside={
                    <>
                        {!blocked ? (
                            <span className="cr-strip-progress">
                                <ResolvedPips total={props.conflicts.length} resolved={props.resolved} />
                                <span>{props.resolved} of {props.conflicts.length} resolved</span>
                            </span>
                        ) : null}
                        {!props.isAuthor && props.onRejectWithConflicts ? (
                            <Button type="secondary" size="sm" text="Reject with these conflicts" onClick={props.onRejectWithConflicts} />
                        ) : null}
                        {props.onCheckAgain ? <TextButton text="Check again" icon={<Icons.refresh size={14} />} onClick={props.onCheckAgain} /> : null}
                    </>
                }
            >
                <strong>
                    {props.attempted ? "Couldn't update: " : ""}
                    {plural(props.conflicts.length, "change conflicts", "changes conflict")} with edits made since
                </strong>
                {props.attempted ? ", nothing was changed" : ""}
                {": "}
                {pages.slice(0, SummaryPages).map(([pageId, n], i) => (
                    <span key={pageId}>
                        {i > 0 ? ", " : ""}
                        <TextButton className="cr-inline-link" text={props.titleOf(pageId)} onClick={() => props.onSelectPage(pageId)} />
                        {pages.length > 1 || n > 1 ? ` (${n})` : ""}
                    </span>
                ))}
                {hidden > 0 ? ` and ${plural(hidden, "more page", "more pages")}` : ""}.
                {" "}
                {blocked
                    ? props.isAuthor
                        ? "Some can't be resolved here: withdraw this change request and propose it again from an up-to-date copy."
                        : `Some can't be resolved here: ${author} has to withdraw it and propose again from an up-to-date copy, or you reject it.`
                    : left === 0
                        ? `All resolved: Update merges the edits made since with your choices. Nothing changes until you do.${props.isAuthor ? "" : " It then counts as your update; the author sees what you decided."}`
                        : props.resolved > 0
                            ? `${plural(left, "conflict", "conflicts")} left: resolve ${left === 1 ? "it on its page" : "them on their pages"}, then Update.`
                            : props.isAuthor
                                ? "Pick a side for each on its page, or edit the lines, then Update."
                                : `Keep ${author === "the author" ? "the author's" : `${author}'s`} or take the page's on each page (or edit the lines), then Update; or reject with the conflicts as the reason.`}
            </StatusStrip>
        </section>
    );
}

/** What the conflict is about: "Title", "Rule “status”", "Content · line 4", "Content · lines 143–172". */
function heading(c: ChangesetConflictDTO) {
    if (c.field === "title") return "Title";
    if (c.field === "rules") return c.path ? `Rule “${c.path}”` : "Rules";
    if (c.field === "content") {
        if (!c.line) return "Content";
        const lines = c.base ? c.base.split("\n").length : 0;
        return lines > 1 ? `Content · lines ${c.line}–${c.line + lines - 1}` : lines === 1 ? `Content · line ${c.line}` : `Content · at line ${c.line}`;
    }
    return "Page";
}

/** Past this many characters on the two sides together, a word diff is not worth its time: the value is shown plain. */
const MarkedLimit = 4000;

/** The value with the words that differ from the base marked (one mark across the spaces between changed words). */
export function Marked(props: { base: string; value: string }) {
    if (props.value === "") return <span className="cr-conflict-empty">(empty)</span>;
    if (props.base.length + props.value.length > MarkedLimit) return <>{props.value}</>;
    const parts = diffWordsWithSpace(props.base, props.value).filter((p) => !p.removed);
    const runs: { text: string; changed: boolean }[] = [];
    parts.forEach((p, i) => {
        const bridge = !p.added && /^[ \t]+$/.test(p.value) && parts[i - 1]?.added && parts[i + 1]?.added;
        const changed = !!p.added || !!bridge;
        const last = runs[runs.length - 1];
        if (last && last.changed === changed) last.text += p.value;
        else runs.push({ text: p.value, changed });
    });
    return <>{runs.map((r, i) => (r.changed ? <mark key={i}>{r.text}</mark> : <Fragment key={i}>{r.text}</Fragment>))}</>;
}

/** Page-level conflicts in the reader's terms (the server's message names page ids). */
const pageMessages: Record<string, string> = {
    "changed-before-delete": "The page was edited after the proposal to delete it.",
    deleted: "The page was deleted after the proposal.",
    "no-base": "The page changed after the proposal, which is too old to merge automatically: withdraw it and propose again.",
};

/** The choices a conflict takes, in the reader's words. */
function choicesFor(c: ChangesetConflictDTO, labels: SideLabels): { choice: Resolution["choice"]; label: string; editable?: boolean }[] {
    if (c.code === "deleted" || c.code === "no-base") return [];
    if (c.field === "page") return [{ choice: ResolutionChoices.Ours, label: "Delete anyway" }];
    if (c.field === "content" && c.code === "changed-before-delete") {
        return [{ choice: ResolutionChoices.Ours, label: "Clear anyway" }, { choice: ResolutionChoices.Theirs, label: "Keep the page's content" }];
    }
    return [
        { choice: ResolutionChoices.Ours, label: labels.keepOurs },
        { choice: ResolutionChoices.Theirs, label: labels.takeTheirs },
        { choice: ResolutionChoices.Manual, label: "Edit", editable: true },
    ];
}

interface IConflictCardProps {
    conflict: ChangesetConflictDTO;
    resolution?: Resolution;
    canResolve: boolean;
    labels: SideLabels;
    onResolve: (resolution: Resolution | undefined) => void;
}

function ConflictCard(props: IConflictCardProps) {
    const { labels } = props;
    const c = props.conflict;
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(c.proposed ?? "");
    const hasValues = c.base != null || c.proposed != null || c.current != null;
    const base = c.base ?? "";
    const choices = props.canResolve ? choicesFor(c, labels) : [];
    const resolvedLabel = props.resolution
        ? c.field === "page" ? "deleted anyway"
            : c.field === "content" && c.code === "changed-before-delete"
                ? props.resolution.choice === ResolutionChoices.Ours ? "cleared anyway" : "the page's content kept"
                : labels.resolved[props.resolution.choice]
        : undefined;
    return (
        <li className={`cr-conflict-card${props.resolution ? " is-resolved" : ""}`}>
            <div className="cr-conflict-head">
                <span className="cr-conflict-icon">{props.resolution ? <Icons.check size={16} /> : <Icons.warning size={16} />}</span>
                <span className="cr-conflict-title">{heading(c)}</span>
                {props.resolution ? <span className="cr-conflict-resolved">Resolved: {resolvedLabel}</span> : null}
                {props.canResolve && props.resolution ? (
                    <TextButton className="cr-conflict-head-aside" text="Change" onClick={() => { props.onResolve(undefined); setEditing(false); }} />
                ) : !props.canResolve && labels.decides && !props.resolution && choicesFor(c, labels).length ? (
                    <span className="cr-conflict-head-aside cr-conflict-decides">{labels.decides}</span>
                ) : null}
            </div>
            {editing ? (
                <div className="cr-conflict-edit">
                    {c.field === "title"
                        ? <TextInput aria-label="Your title" value={draft} onChange={setDraft} />
                        : (
                            <Field label={c.field === "rules" ? "Your rule value (JSON)" : "Your lines"}>
                                <TextArea value={draft} rows={3} onChange={setDraft} />
                            </Field>
                        )}
                    <div className="cr-conflict-choices">
                        <Button type="secondary" size="sm" text="Use this" onClick={() => { props.onResolve({ choice: ResolutionChoices.Manual, value: draft }); setEditing(false); }} />
                        <TextButton type="white" text="Cancel" onClick={() => setEditing(false)} />
                    </div>
                </div>
            ) : hasValues ? (
                <dl className="cr-conflict-values">
                    <div className="cr-conflict-value is-ours">
                        <dt>{labels.ours}</dt>
                        <dd><pre><Marked base={base} value={c.proposed ?? ""} /></pre></dd>
                    </div>
                    <div className="cr-conflict-value is-theirs">
                        <dt>{labels.theirs}</dt>
                        <dd><pre><Marked base={base} value={c.current ?? ""} /></pre></dd>
                    </div>
                    <div className="cr-conflict-value is-was">
                        <dt>Was</dt>
                        <dd><pre>{base === "" ? <span className="cr-conflict-empty">(nothing)</span> : base}</pre></dd>
                    </div>
                    {props.resolution?.choice === ResolutionChoices.Manual ? (
                        <div className="cr-conflict-value is-edited">
                            <dt>Edited</dt>
                            <dd><pre><Marked base={base} value={props.resolution.value ?? ""} /></pre></dd>
                        </div>
                    ) : null}
                </dl>
            ) : (
                <div className="cr-conflict-message">{pageMessages[c.code] ?? c.message}</div>
            )}
            {!editing && !props.resolution && choices.length ? (
                <div className="cr-conflict-choices">
                    {choices.map((ch) => (
                        ch.editable
                            ? <TextButton key={ch.choice} text={ch.label} icon={<Icons.edit size={14} />} onClick={() => { setDraft(c.proposed ?? ""); setEditing(true); }} />
                            : <Button key={ch.choice} type="secondary" size="sm" text={ch.label} onClick={() => props.onResolve({ choice: ch.choice })} />
                    ))}
                </div>
            ) : null}
        </li>
    );
}

interface IPageConflictsProps {
    conflicts: ChangesetConflictDTO[];
    resolutions: Resolutions;
    /** The author of an open change request resolves; everyone else reads. */
    canResolve: boolean;
    onResolve: (key: string, resolution: Resolution | undefined) => void;
    labels: SideLabels;
}

/**
 * The conflicts of one page that are not line conflicts of its content (those are shown in the merged content): title, rules, a delete
 * or clear of a page edited since. Compact: what the author wrote, what the page has now (the words each changed marked), what both
 * started from, and the author's choice. The first few are shown, the rest on request.
 */
export function PageConflicts(props: IPageConflictsProps) {
    const [all, setAll] = useState(false);
    const shown = all ? props.conflicts : props.conflicts.slice(0, FirstShown);
    const rest = props.conflicts.length - shown.length;
    const resolved = props.conflicts.filter((c) => props.resolutions[conflictKey(c)]).length;
    return (
        <section className="cr-field cr-conflicts" aria-label="Conflicts on this page">
            <h4 className="cr-field-label">
                {plural(props.conflicts.length, "conflict", "conflicts")} with edits made since
                {resolved ? ` · ${resolved} resolved` : ""}
            </h4>
            <ol className="cr-conflict-list">
                {shown.map((c) => {
                    const key = conflictKey(c);
                    return <ConflictCard key={key} conflict={c} resolution={props.resolutions[key]} canResolve={props.canResolve} labels={props.labels} onResolve={(r) => props.onResolve(key, r)} />;
                })}
            </ol>
            {rest > 0 ? (
                <TextButton className="cr-conflict-more" text={`Show ${rest} more`} onClick={() => setAll(true)} />
            ) : null}
        </section>
    );
}
